import React, { useState } from 'react';
import { Card, List, Alert, Spin, Empty, Typography, Pagination, Button } from 'antd';
import { CheckCircleOutlined, FileSearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { CompletedSurveyItem } from '../../types/participant.types';
import ResponseAuditModal from '../survey/ResponseAuditModal';

const { Text } = Typography;

interface CompletedSurveysCardProps {
  surveys: CompletedSurveyItem[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
  isLoading?: boolean;
  error?: string | null;
  onPageChange?: (page: number, pageSize: number) => void;
  /** When true, renders without a Card wrapper (for use inside tabs) */
  noCard?: boolean;
  hasTagFilter?: boolean;
}

/**
 * CompletedSurveysCard Component (US3)
 *
 * Displays paginated list of surveys user has completed with submission dates.
 * Accepts `noCard` prop to skip the Card wrapper when embedded inside a Tab.
 */
const CompletedSurveysCardComponent: React.FC<CompletedSurveysCardProps> = ({
  surveys,
  total,
  page,
  pageSize,
  totalPages,
  isLoading = false,
  error = null,
  onPageChange,
  noCard = false,
  hasTagFilter = false,
}) => {
  const [auditModalOpen, setAuditModalOpen] = useState(false);
  const [selectedSurvey, setSelectedSurvey] = useState<CompletedSurveyItem | null>(null);

  const handleOpenAudit = (survey: CompletedSurveyItem) => {
    setSelectedSurvey(survey);
    setAuditModalOpen(true);
  };

  const handleCloseAudit = () => {
    setAuditModalOpen(false);
    setSelectedSurvey(null);
  };

  const cardStyle = { height: '100%' };
  const emptyBodyStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 280,
    textAlign: 'center',
    gap: 8,
  };

  if (isLoading) {
    const content = (
      <div style={emptyBodyStyle}>
        <Spin size="large" />
        <Text style={{ marginTop: 8 }}>Carregando histórico...</Text>
      </div>
    );
    return noCard ? content : <Card title="Histórico de Pesquisas" style={cardStyle}>{content}</Card>;
  }

  if (error) {
    const content = (
      <div style={{ minHeight: 280, display: 'flex', alignItems: 'center' }}>
        <Alert
          type="error"
          message="Erro ao carregar histórico"
          description={error}
          showIcon
          style={{ width: '100%' }}
        />
      </div>
    );
    return noCard ? content : <Card title="Histórico de Pesquisas" style={cardStyle}>{content}</Card>;
  }

  if (total === 0) {
    const content = (
      <Empty
        description={
          <span>
            <strong style={{ color: '#595959' }}>Nenhuma pesquisa respondida</strong>
            <br />
            <span style={{ color: '#8c8c8c', fontSize: 13 }}>
              {hasTagFilter
                ? 'Nenhuma pesquisa respondida encontrada para as etiquetas selecionadas.'
                : 'Suas respostas aparecerão aqui após participar de pesquisas.'}
            </span>
          </span>
        }
        style={{ margin: '40px 0', minHeight: 200, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}
      />
    );
    return noCard ? content : <Card title="Histórico de Pesquisas" style={cardStyle}>{content}</Card>;
  }

  const listContent = (
    <>
      <List
        dataSource={surveys}
        renderItem={(survey) => (
          <List.Item
            actions={[
              <Button
                key="audit"
                type="link"
                icon={<FileSearchOutlined />}
                onClick={() => handleOpenAudit(survey)}
              >
                Ver Auditoria
              </Button>,
            ]}
          >
            <List.Item.Meta
              avatar={<CheckCircleOutlined style={{ fontSize: 20, color: '#52c41a' }} />}
              title={survey.title}
              description={
                <Text type="secondary">
                  Respondida em {dayjs(survey.submittedAt).format('DD/MM/YYYY HH:mm')}
                </Text>
              }
            />
          </List.Item>
        )}
      />

      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 16 }}>
          <Pagination
            current={page}
            pageSize={pageSize}
            total={total}
            onChange={(nextPage, nextPageSize) => onPageChange?.(nextPage, nextPageSize)}
            showSizeChanger={false}
          />
        </div>
      )}

      {selectedSurvey && (
        <ResponseAuditModal
          open={auditModalOpen}
          onClose={handleCloseAudit}
          responseHash={selectedSurvey.responseHash}
          surveyName={selectedSurvey.title}
          surveyPublicAuditHash={selectedSurvey.publicAuditHash}
        />
      )}
    </>
  );

  if (noCard) return listContent;

  return (
    <Card
      title={`${total} ${total === 1 ? 'pesquisa respondida' : 'pesquisas respondidas'}`}
      style={cardStyle}
    >
      {listContent}
    </Card>
  );
};

export const CompletedSurveysCard = React.memo(CompletedSurveysCardComponent);
